import { useEffect, useId, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useScreenRequest } from '../hooks/useScreenRequest';
import { InlineError, LoadingState, StaleNotice } from '../components/PageStates';
import { NOT_RECORDED, formatUtc, orNotRecorded, targetText } from './auditFields';
import './Audit.css';

const SOURCE = { recorded: 'Recorded at the time', current: 'Current operator record (not recorded at the time)' };

/** A labelled key/value section. `rows === null` means the entry never recorded it. */
function Fields({ title, rows }) {
  const id = useId();
  return (
    <section className="card audit-fields" aria-labelledby={id}>
      <h3 id={id}>{title}</h3>
      {rows === null || rows.length === 0 ? <p>{NOT_RECORDED}</p> : (
        <dl>{rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
      )}
    </section>
  );
}

/** Before/after metadata as text rows. Nested values are shown as JSON text, never as markup. */
function stateRows(state) {
  if (!state) return null;
  return Object.entries(state).map(([k, v]) => [k, v === null ? NOT_RECORDED : typeof v === 'object' ? JSON.stringify(v) : String(v)]);
}

export default function AuditDetail() {
  const { id } = useParams();
  const location = useLocation();
  const { read } = useScreenRequest(id);
  const [loaded, setLoaded] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(null);
    read((options) => api.getAuditEntry(id, options), {
      success: (data) => setLoaded({ id, entry: data.entry, at: new Date() }),
      error: setError,
      settled: () => setLoading(false),
    });
  }, [id, read, reload]);

  // Back returns to the list query and page this entry was opened from, or to Audit.
  const listState = location.state?.auditList;
  const entry = loaded?.id === id ? loaded.entry : null;
  // Only the server's explicit id-validation refusal shares the missing-entry presentation.
  const invalidId = error?.status === 400 && error.data?.statusCode === 400
    && error.data?.type === 'Validation error' && typeof error.data?.error === 'string';
  const notFound = error?.status === 404 || invalidId;

  return (
    <div className="audit-page">
      <Link className="btn btn-outline" to="/audit" state={listState ? { auditList: listState } : undefined}>Back to audit</Link>
      {/* One heading from the first paint, so navigation has somewhere to put focus (§7.6). */}
      <h2>{notFound ? 'Entry not found' : `Audit entry ${id}`}</h2>

      {loading && <LoadingState label="Loading audit entry" lines={8} />}
      {!loading && notFound && (
        <div className="page-state page-empty">
          <p>No audit entry has this id.</p>
        </div>
      )}
      {!loading && error && !notFound && (
        <>
          {entry && <StaleNotice loadedAt={loaded.at} />}
          <InlineError message={error.message} onAction={() => setReload((n) => n + 1)} />
        </>
      )}

      {!loading && entry && !notFound && (
        <>
          <Fields title="Summary" rows={[
            ['Action', entry.action],
            ['Category', entry.category],
            ['Time', formatUtc(entry.createdAt)],
            ['Target', targetText(entry.target)],
            ['Tenant', orNotRecorded(entry.tenantId)],
            ['Request ID', orNotRecorded(entry.requestId)],
            ['Session ID', orNotRecorded(entry.sessionId)],
            ['Reason', orNotRecorded(entry.reason)],
            ...(entry.passwordChanged ? [['Password changed', 'Yes']] : []),
          ]} />
          <Fields title="Operator" rows={entry.actor ? [
            ['Name', orNotRecorded(entry.actor.displayName)],
            ['Email', orNotRecorded(entry.actor.email)],
            ['Operator ID', orNotRecorded(entry.actor.id)],
            ['Source', SOURCE[entry.actor.source] || NOT_RECORDED],
          ] : null} />
          <div className="audit-states">
            <Fields title="Before" rows={stateRows(entry.before)} />
            <Fields title="After" rows={stateRows(entry.after)} />
          </div>
        </>
      )}
    </div>
  );
}
